import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { categoryLabel } from "@/components/scam";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users, MessageSquare, Code, Link2, Phone, Trash2, ExternalLink, Shield, Save, Settings2, Send, Globe, CheckCircle2, AlertTriangle,
} from "lucide-react";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-950/40 grid place-items-center"><Icon className="h-5 w-5 text-blue-600" /></div>
        <div>
          <div className="text-2xl font-extrabold font-display text-slate-900 dark:text-white">{value ?? "—"}</div>
          <div className="text-xs text-slate-400">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [overview, setOverview] = useState(null);
  const [reports, setReports] = useState([]);
  const [sites, setSites] = useState([]);
  const [links, setLinks] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ site_title: "", site_description: "", gsc_verification_code: "" });
  const [savingSeo, setSavingSeo] = useState(false);
  const [gStatus, setGStatus] = useState(null);
  const [gUrl, setGUrl] = useState("");
  const [gBusy, setGBusy] = useState(false);

  const loadAll = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/admin/links").then((r) => setLinks(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/seo/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/seo/google/status").then((r) => setGStatus(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const delReport = async (id) => { await api.delete(`/admin/reports/${id}`); toast.success("Yorum silindi"); loadAll(); };
  const delSite = async (id) => { await api.delete(`/sites/${id}`); toast.success("Sayfa silindi"); loadAll(); };
  const delLink = async (id) => { await api.delete(`/links/${id}`); toast.success("Link silindi"); loadAll(); };

  const saveSeo = async () => {
    setSavingSeo(true);
    try {
      await api.put("/seo/settings", settings);
      toast.success("SEO ayarları kaydedildi");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingSeo(false); }
  };

  const seoUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api${p}`;

  const notifyGoogle = async () => {
    const url = gUrl.trim() || "https://sorgupanel.org";
    setGBusy(true);
    try {
      const { data } = await api.post("/seo/google/notify", { url, type: "URL_UPDATED" });
      toast.success(data.message);
      setGUrl("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGBusy(false); }
  };

  const submitSitemap = async () => {
    setGBusy(true);
    try {
      const { data } = await api.post("/seo/google/sitemap");
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-2 mb-8">
        <Shield className="h-6 w-6 text-blue-600" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Yönetici Paneli</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Users} label="Kullanıcı" value={overview?.users} />
        <StatCard icon={MessageSquare} label="Yorum/Rapor" value={overview?.reports} />
        <StatCard icon={Phone} label="Numara" value={overview?.numbers} />
        <StatCard icon={Code} label="HTML Sayfa" value={overview?.sites} />
        <StatCard icon={Link2} label="Kısa Link" value={overview?.links} />
      </div>

      <Tabs defaultValue="reports">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Yorum Moderasyonu</TabsTrigger>
          <TabsTrigger value="sites" data-testid="admin-tab-sites">HTML Sayfalar</TabsTrigger>
          <TabsTrigger value="links" data-testid="admin-tab-links">Linkler</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Kullanıcılar</TabsTrigger>
          <TabsTrigger value="seo" data-testid="admin-tab-seo">Site SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-3">
          {reports.length === 0 && <p className="text-slate-400 text-center py-8">Yorum yok.</p>}
          {reports.map((r) => (
            <div key={r.id} data-testid={`admin-report-${r.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono font-semibold text-slate-900 dark:text-white">{r.number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.vote === "scam" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>{r.vote === "scam" ? "Dolandırıcı" : "Güvenli"}</span>
                  <span className="text-xs text-blue-600">{categoryLabel(r.category)}</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">{r.comment}</p>
                <p className="text-xs text-slate-400 mt-1">— {r.user_name}</p>
              </div>
              <button data-testid={`admin-del-report-${r.id}`} onClick={() => delReport(r.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="sites" className="space-y-3">
          {sites.length === 0 && <p className="text-slate-400 text-center py-8">Sayfa yok.</p>}
          {sites.map((s) => (
            <div key={s.id} data-testid={`admin-site-${s.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 dark:text-white truncate">{s.title}</div>
                <a href={s.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-blue-600 inline-flex items-center gap-1">/{s.slug} <ExternalLink className="h-3 w-3" /></a>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{s.user_name}</span>
              <button data-testid={`admin-del-site-${s.id}`} onClick={() => delSite(s.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="links" className="space-y-3">
          {links.length === 0 && <p className="text-slate-400 text-center py-8">Link yok.</p>}
          {links.map((l) => (
            <div key={l.id} data-testid={`admin-link-${l.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm text-blue-600 truncate">{l.short_url}</div>
                <div className="text-xs text-slate-400 truncate">{l.target_url}</div>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{l.clicks} tık</span>
              <button data-testid={`admin-del-link-${l.id}`} onClick={() => delLink(l.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-blue-600 text-white text-sm font-bold grid place-items-center">{u.name?.[0]?.toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 dark:text-white">{u.name}</div>
                <div className="text-xs text-slate-400 truncate">{u.email}</div>
              </div>
              {u.role === "admin" && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-600">ADMIN</span>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="seo">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 space-y-4">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Settings2 className="h-5 w-5 text-blue-600" /> Genel Site SEO</h3>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Site Başlığı</label>
                <Input data-testid="admin-site-title" value={settings.site_title || ""} onChange={(e) => setSettings({ ...settings, site_title: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Site Açıklaması</label>
                <Textarea data-testid="admin-site-desc" value={settings.site_description || ""} onChange={(e) => setSettings({ ...settings, site_description: e.target.value })} rows={2} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Google Search Console Doğrulama Kodu</label>
                <Input data-testid="admin-gsc-code" value={settings.gsc_verification_code || ""} onChange={(e) => setSettings({ ...settings, gsc_verification_code: e.target.value })} placeholder="google-site-verification içeriği" className="font-mono text-sm" />
                <p className="text-xs text-slate-400 mt-1">Bu kod tüm sayfaların &lt;head&gt; bölümüne meta olarak eklenir.</p>
              </div>
              <Button data-testid="admin-save-seo" onClick={saveSeo} disabled={savingSeo} className="bg-blue-600 hover:bg-blue-700"><Save className="h-4 w-4 mr-2" /> {savingSeo ? "Kaydediliyor..." : "Kaydet"}</Button>
            </div>

            <div className="space-y-4">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Send className="h-5 w-5 text-blue-600" /> Google'a İndeks Bildir</h3>
                  {gStatus && (gStatus.configured
                    ? <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-3 w-3" /> BAĞLI</span>
                    : <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-600"><AlertTriangle className="h-3 w-3" /> YAPILANDIRILMADI</span>)}
                </div>

                {gStatus && !gStatus.configured && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-400">
                    Gerçek Google bildirimi için servis hesabı anahtarı (GOOGLE_SERVICE_ACCOUNT_JSON) eklenmeli ve servis hesabı Search Console'da sahip olarak tanımlanmalı.
                  </div>
                )}
                {gStatus?.service_account_email && (
                  <div className="text-xs text-slate-500">Servis hesabı: <span className="font-mono">{gStatus.service_account_email}</span></div>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <Input data-testid="admin-google-url" value={gUrl} onChange={(e) => setGUrl(e.target.value)} placeholder="https://sorgupanel.org/sayfam" className="font-mono text-sm h-10" />
                  <Button data-testid="admin-google-notify" onClick={notifyGoogle} disabled={gBusy} className="h-10 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shrink-0"><Send className="h-4 w-4 mr-1.5" /> Bildir</Button>
                </div>
                <Button data-testid="admin-google-sitemap" onClick={submitSitemap} disabled={gBusy} variant="outline" className="w-full"><Globe className="h-4 w-4 mr-2" /> Sitemap'i Google'a Gönder</Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <a href="/sitemap.xml" target="_blank" rel="noreferrer" data-testid="admin-sitemap-link" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 hover:shadow-md hover:border-blue-300 transition-all group">
                  <Globe className="h-5 w-5 text-blue-600 mb-2" />
                  <h4 className="font-semibold text-sm group-hover:text-blue-600">Sitemap.xml</h4>
                  <p className="text-xs text-slate-500 font-mono truncate">sorgupanel.org/sitemap.xml</p>
                </a>
                <a href="/robots.txt" target="_blank" rel="noreferrer" data-testid="admin-robots-link" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 hover:shadow-md hover:border-blue-300 transition-all group">
                  <Code className="h-5 w-5 text-blue-600 mb-2" />
                  <h4 className="font-semibold text-sm group-hover:text-blue-600">Robots.txt</h4>
                  <p className="text-xs text-slate-500 font-mono truncate">sorgupanel.org/robots.txt</p>
                </a>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
